import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertCircle, Database } from 'lucide-react';
import { API_CONFIG } from '../../../config/constants';

interface ViewDataModalProps {
  datasourceId: string;
  datasourceName: string;
  onClose: () => void;
}

interface ViewDataResponse {
  columns: string[];
  rows: any[][];
  totalRows: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  fetchedAt: string;
}

const extractObjectLabel = (value: Record<string, unknown>): string | null => {
  const directKeys = ['name', 'label', 'id'] as const;
  for (const key of directKeys) {
    const directValue = value[key];
    if (directValue !== undefined && directValue !== null) {
      return String(directValue);
    }
  }

  for (const nested of Object.values(value)) {
    if (nested && typeof nested === 'object') {
      const nestedRecord = nested as Record<string, unknown>;
      for (const key of directKeys) {
        const nestedValue = nestedRecord[key];
        if (nestedValue !== undefined && nestedValue !== null) {
          return String(nestedValue);
        }
      }
    }
  }

  return null;
};

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }

    const isObjectArray = value.every(
      (item) => item !== null && typeof item === 'object' && !Array.isArray(item)
    );

    if (isObjectArray) {
      return value
        .map((item) => {
          const label = extractObjectLabel(item as Record<string, unknown>);
          return label ?? '[object]';
        })
        .join(', ');
    }

    return value.map((item) => String(item)).join(', ');
  }

  if (typeof value === 'object') {
    const label = extractObjectLabel(value as Record<string, unknown>);
    if (label) {
      return label;
    }

    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) {
      return '[object]';
    }
    const summaryKeys = keys.slice(0, 3).join(', ');
    const suffix = keys.length > 3 ? ', …' : '';
    return `{${summaryKeys}${suffix}}`;
  }

  return String(value);
};

export const ViewDataModal: React.FC<ViewDataModalProps> = ({
  datasourceId,
  datasourceName,
  onClose,
}) => {
  const [data, setData] = useState<ViewDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const fetchData = async (pageNum: number) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const empresaId = localStorage.getItem('empresaId');

      console.log('[ViewDataModal] Fetching data:', {
        datasourceId,
        page: pageNum,
        pageSize,
        empresaId,
        hasToken: !!token
      });

      if (!empresaId) {
        throw new Error('No se encontró empresaId en localStorage. Por favor, recarga la página.');
      }

      const url = `${API_CONFIG.BASE_URL}/api/datasources/${datasourceId}/view?page=${pageNum}&pageSize=${pageSize}`;
      console.log('[ViewDataModal] Fetching URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-Id': empresaId,
        },
      });

      console.log('[ViewDataModal] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[ViewDataModal] Error response:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to fetch data');
      }

      const result: ViewDataResponse = await response.json();
      console.log('[ViewDataModal] Data fetched successfully:', {
        columns: result.columns.length,
        rows: result.rows.length,
        totalRows: result.totalRows
      });
      setData(result);
      setPage(pageNum);
    } catch (err: any) {
      console.error('[ViewDataModal] Error viewing datasource:', err);
      setError(err.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [datasourceId]);

  const handleRefresh = () => {
    fetchData(page);
  };

  const handlePrevPage = () => {
    if (page > 1) {
      fetchData(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.hasMore) {
      fetchData(page + 1);
    }
  };

  const visibleColumnIndexes = data?.columns.reduce<number[]>((acc, column, index) => {
    if (!column.toLowerCase().includes('_raw')) {
      acc.push(index);
    }
    return acc;
  }, []);

  const visibleColumns = visibleColumnIndexes?.map((index) => data?.columns[index] ?? '') ?? [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl border border-slate-700 w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-sky-500" />
            <div>
              <h2 className="text-xl font-semibold text-white">
                Ver Datos
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                {datasourceName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col p-6">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-sky-500 animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Cargando datos...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  Error al cargar datos
                </h3>
                <p className="text-slate-400 mb-4">{error}</p>
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Info Bar */}
              <div className="flex items-center justify-between mb-4 text-sm text-slate-400">
              <div className="flex items-center gap-4">
                <span>
                  <strong className="text-white">{data.totalRows.toLocaleString()}</strong> filas totales
                </span>
                <span>
                  <strong className="text-white">{visibleColumns.length}</strong> columnas
                </span>
                <span className="text-xs text-slate-500">
                  Actualizado: {new Date(data.fetchedAt).toLocaleTimeString()}
                </span>
              </div>
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 px-3 py-1.5 text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors"
                  title="Refrescar datos"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refrescar
                </button>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-auto border border-slate-700 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 sticky top-0">
                    <tr>
                      {visibleColumns.map((col, idx) => (
                        <th
                          key={idx}
                          className="px-4 py-3 text-left font-semibold text-sky-400 border-b border-slate-700 whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className="hover:bg-slate-800/50 transition-colors border-b border-slate-700 last:border-b-0"
                      >
                        {visibleColumnIndexes?.map((cellIdx) => (
                          <td
                            key={cellIdx}
                            className="px-4 py-3 text-slate-300 whitespace-nowrap"
                          >
                            {row[cellIdx] !== null && row[cellIdx] !== undefined ? (
                              formatCellValue(row[cellIdx])
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                <div className="text-sm text-slate-400">
                  Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, data.totalRows)} de {data.totalRows.toLocaleString()}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className="px-4 py-2 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!data.hasMore}
                    className="px-4 py-2 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
